import type { ApiClient } from './client';
import { SseParser, reconnectDelayMs } from './sse';
import type { TransportStream } from './transport';
import type { EventFilter, PlatformEvent } from './types';

export type EventStreamState =
  | 'connecting'
  | 'open'
  /** 切断を検知して待機中。cursorは保持している。 */
  | 'reconnecting'
  /** cursorが保持期間(7日)を過ぎた。snapshotの取り直しが必要。 */
  | 'expired'
  | 'closed';

/** 購読できるイベントの最小の形。重複排除に id、再開に cursor を使う。 */
export interface StreamEvent {
  id: string;
  cursor: string;
}

export interface EventStreamHandlers<E extends StreamEvent = PlatformEvent> {
  onEvent(event: E): void;
  onState(state: EventStreamState, detail?: string): void;
}

export interface EventStreamOptions {
  workspaceId: string;
  filter: EventFilter;
  /** snapshot応答のeventCursor。省略すると接続時点以後のみ受け取る。 */
  cursor?: string;
  /** テスト用。実時間を待たずに再接続させる。 */
  sleep?: (ms: number) => Promise<void>;
  /** heartbeatが途絶えたと判断するまでの時間。0で無効。 */
  idleTimeoutMs?: number;
  /** 購読先を差し替える（studio の W/studio/events など）。省略すると platform の W/events を filter 付きで開く。 */
  open?: (cursor: string | undefined, signal: AbortSignal) => Promise<TransportStream>;
}

const DEDUPE_CAPACITY = 512;
const DEFAULT_IDLE_TIMEOUT_MS = 45_000; // heartbeat 15秒の3回分
/**
 * chunkの処理を続けたままにしてよい時間。
 * fetchのstreamはI/O待ちで自然に制御が戻るが、in-processのtransport（fixture等）は
 * microtaskだけでchunkを供給できるため、timerと描画に順番が回らなくなり得る。
 * この上限を超えたらmacrotaskへ明示的に戻す。
 */
const YIELD_AFTER_MS = 50;

/**
 * 永続SSEの購読。
 * - cursorだけを使い、Last-Event-IDヘッダは送らない（同時指定は400のため）。
 * - at-least-onceなのでevent IDで重複排除する。
 * - 410は再接続では回復しないので expired を通知し、呼び出し側にsnapshot再取得させる。
 * - filterを変えるときは新しいEventStreamを作る。ここでは変更できない。
 */
export class EventStream<E extends StreamEvent = PlatformEvent> {
  private controller: AbortController | null = null;
  private activeStream: { close(): void } | null = null;
  private closed = false;
  private cursor: string | undefined;
  private attempt = 0;
  /** 直近の切断理由。再接続の原因を画面へ出すために持ち回る。 */
  private lastFailure: string | null = null;
  private readonly seen = new Set<string>();
  private readonly seenOrder: string[] = [];
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly idleTimeoutMs: number;

  constructor(
    private readonly api: ApiClient,
    private readonly options: EventStreamOptions,
    private readonly handlers: EventStreamHandlers<E>,
  ) {
    this.cursor = options.cursor;
    this.sleep = options.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    this.idleTimeoutMs = options.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
  }

  /** 接続ループを開始する。戻り値はループ終了のPromiseで、通常は待たない。 */
  async start(): Promise<void> {
    while (!this.closed) {
      const outcome = await this.connectOnce();
      if (this.closed) break;
      if (outcome === 'expired') {
        this.handlers.onState('expired');
        return;
      }
      this.attempt = outcome === 'clean' ? 0 : this.attempt + 1;
      const delay = reconnectDelayMs(this.attempt);
      const cause = this.lastFailure ?? '接続が終了しました';
      this.handlers.onState('reconnecting', `${cause}。${delay}ms後に再接続`);
      await this.sleep(delay);
    }
    this.handlers.onState('closed');
  }

  close(): void {
    this.closed = true;
    this.controller?.abort();
    this.controller = null;
    // abortを見ないtransportもあるので、stream自体にも終了を伝える。
    this.activeStream?.close();
    this.activeStream = null;
  }

  /** 現在までに受け取った最後のcursor。snapshot再取得後の再購読に使う。 */
  get lastCursor(): string | undefined {
    return this.cursor;
  }

  private async connectOnce(): Promise<'clean' | 'error' | 'expired'> {
    this.handlers.onState('connecting');
    this.controller = new AbortController();
    const parser = new SseParser();
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const resetIdle = () => {
      if (this.idleTimeoutMs <= 0) return;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => this.controller?.abort(), this.idleTimeoutMs);
    };

    try {
      const signal = this.controller.signal;
      const stream = this.options.open
        ? await this.options.open(this.cursor, signal)
        : await this.api.openEvents(this.options.workspaceId, { ...this.options.filter, cursor: this.cursor }, signal);
      this.activeStream = stream;
      if (stream.status === 410) {
        stream.close();
        return 'expired';
      }
      if (stream.status !== 200) {
        this.lastFailure = `購読が${stream.status}を返しました`;
        stream.close();
        return 'error';
      }
      this.lastFailure = null;
      this.handlers.onState('open');
      resetIdle();
      let sliceStartedAt = Date.now();
      for await (const chunk of stream.chunks()) {
        resetIdle();
        if (Date.now() - sliceStartedAt > YIELD_AFTER_MS) {
          // macrotaskへ制御を戻す。ここを挟まないと同じタブのtimerとUIが止まる。
          await new Promise((resolve) => setTimeout(resolve, 0));
          sliceStartedAt = Date.now();
        }
        for (const frame of parser.push(chunk)) {
          if (frame.event === 'stream.reset') {
            // 遅い購読者としてサーバーが切ったケース。保持しているcursorから再開する。
            this.lastFailure = 'サーバーがstreamを再設定しました';
            stream.close();
            return 'error';
          }
          this.dispatch(frame.data);
        }
      }
      return 'clean';
    } catch (error) {
      this.lastFailure = error instanceof Error ? error.message : String(error);
      return 'error';
    } finally {
      if (idleTimer) clearTimeout(idleTimer);
      this.activeStream?.close();
      this.activeStream = null;
      parser.reset();
    }
  }

  /** event行の名前はpayloadのtypeと同じ値が来る契約なので、判定はpayloadだけを見る。 */
  private dispatch(data: string): void {
    if (data === '') return; // heartbeat等
    let parsed: E;
    try {
      parsed = JSON.parse(data) as E;
    } catch {
      return; // 解釈できないフレームは無視する。状態はsnapshotから取り直せる。
    }
    if (typeof parsed?.id !== 'string' || typeof parsed?.cursor !== 'string') return;
    this.cursor = parsed.cursor;
    if (this.seen.has(parsed.id)) return;
    this.remember(parsed.id);
    this.handlers.onEvent(parsed);
  }

  private remember(id: string): void {
    this.seen.add(id);
    this.seenOrder.push(id);
    while (this.seenOrder.length > DEDUPE_CAPACITY) {
      const evicted = this.seenOrder.shift();
      if (evicted !== undefined) this.seen.delete(evicted);
    }
  }
}
